/**
 * Shim for @forge/bridge — replaces all Forge bridge calls with demo equivalents
 */

// invoke() — the main Forge resolver call
export async function invoke(functionName: string, payload?: any): Promise<any> {
  switch (functionName) {
    case 'enqueueGenerationJob': {
      // Notify parent frame that a generation was triggered
      try {
        window.parent.postMessage({ type: 'sg-demo-generation' }, '*');
      } catch (_) { /* ignore */ }
      const res = await fetch('/api/demo/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Generation request failed');
      }
      return res.json();
    }
    case 'getGenerationJobStatus': {
      const { requestId } = payload || {};
      const res = await fetch(`/api/demo/status/${requestId}`);
      if (!res.ok) {
        return { requestId, status: 'failed', error: 'Status check failed' };
      }
      return res.json();
    }
    case 'asyncPost': {
      // Direct API post — shouldn't happen in demo, but handle gracefully
      console.warn('[demo shim] asyncPost called:', payload?.url);
      throw new Error('Direct API calls are not available in demo mode');
    }
    case 'asyncGet': {
      console.warn('[demo shim] asyncGet called:', payload?.url);
      throw new Error('Direct API calls are not available in demo mode');
    }
    case 'fetchProjectIssueTypes': {
      // Return all issue types as available
      return [
        { id: '1', name: 'Epic' },
        { id: '2', name: 'Story' },
        { id: '3', name: 'Task' },
      ];
    }
    default:
      console.warn(`[demo shim] Unknown invoke: ${functionName}`, payload);
      return null;
  }
}

// requestJira() — stub that returns mock data
export async function requestJira(url: string, _options?: any): Promise<any> {
  console.warn('[demo shim] requestJira called:', url);
  // Return a mock Response-like object
  return {
    json: async () => {
      // For /rest/api/3/myself
      if (url.includes('/myself')) {
        return {
          accountId: 'demo-user',
          displayName: 'Demo User',
          emailAddress: 'demo@storygenie.com.au',
          avatarUrls: {}
        };
      }
      // For recent projects
      if (url.includes('project/recent') || url.includes('project/search')) {
        return [{
          id: 'demo-project-1',
          key: 'DEMO',
          name: 'Demo Project',
          projectTypeKey: 'software'
        }];
      }
      // For loading epics
      if (url.includes('search')) {
        return { issues: [], isLast: true };
      }
      // For get project
      if (url.includes('/project/')) {
        return {
          id: 'demo-project-1',
          key: 'DEMO',
          name: 'Demo Project',
          issueTypes: [
            { id: '1', name: 'Epic' },
            { id: '2', name: 'Story' },
            { id: '3', name: 'Task' },
          ]
        };
      }
      return {};
    },
    ok: true,
    status: 200,
    text: async () => '{}'
  };
}

// view — theme handling
export const view = {
  theme: {
    enable: async () => {
      // Force dark mode for demo
      document.documentElement.setAttribute('data-color-mode', 'dark');
    }
  },
  getContext: async () => ({
    extension: { type: 'jira:projectPage' }
  })
};

// router — navigation
export const router = {
  open: async (url: string) => {
    window.open(url, '_blank');
  },
  navigate: async (url: string) => {
    window.open(url, '_blank');
  }
};
